import { useState, useEffect } from "react";
import {
  categoriesService,
  articlesService,
  Category,
  Article,
  supabase,
} from "@/lib/supabase";

export interface ArticleWithCategory extends Article {
  category_name?: string;
}

interface UseHomeReturn {
  categories: Category[];
  articles: ArticleWithCategory[];
  isLoading: boolean;
  error: string | null;
  categoryMap: Record<string, string>;
}

export const useHome = (): UseHomeReturn => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [articles, setArticles] = useState<ArticleWithCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [categoryMap, setCategoryMap] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Lấy categories trước
        const categoriesData = await categoriesService.getAll();

        if (!categoriesData || categoriesData.length === 0) {
          // Nếu categories trống, tạo một số dữ liệu mẫu để test
          const sampleCategories: Category[] = [
            {
              id: "1",
              slug: "smart-living",
              name: "Smart Living",
              created_at: new Date().toISOString(),
            },
            {
              id: "2",
              slug: "beauty",
              name: "Beauty",
              created_at: new Date().toISOString(),
            },
            {
              id: "3",
              slug: "health",
              name: "Health",
              created_at: new Date().toISOString(),
            },
            {
              id: "4",
              slug: "shop",
              name: "Shop",
              created_at: new Date().toISOString(),
            },
          ];

          setCategories(sampleCategories);

          // Tạo map từ dữ liệu mẫu
          const sampleCatMap: Record<string, string> = {};
          sampleCategories.forEach((cat) => {
            sampleCatMap[cat.id] = cat.name;
          });
          setCategoryMap(sampleCatMap);
        } else {
          setCategories(categoriesData);

          // Tạo map ID -> Name cho categories
          const catMap: Record<string, string> = {};
          categoriesData.forEach((cat) => {
            catMap[cat.id] = cat.name;
          });
          setCategoryMap(catMap);
        }

        // Lấy articles với join vào categories
        const { data: articlesData, error: articlesError } = await supabase
          .from("articles")
          .select(
            `
            *,
            categories:category_id (
              name
            )
          `
          )
          .order("date", { ascending: false });

        if (articlesError) {
          // Fallback to regular articles if join fails
          const regularArticlesData = await articlesService.getAll();

          if (regularArticlesData && regularArticlesData.length > 0) {
            setArticles(regularArticlesData);
          } else {
            setError("Không thể tải dữ liệu bài viết");
          }
        } else {
          // Transform joined data
          const articlesWithCategory = articlesData.map((article) => {
            const categoryName =
              article.categories?.name ||
              categoryMap[article.category_id] ||
              "Unknown";

            return {
              ...article,
              category_name: categoryName,
            };
          });

          setArticles(articlesWithCategory);
        }
      } catch (error) {
        console.error("Lỗi không xác định khi tải dữ liệu:", error);
        setError("Lỗi khi tải dữ liệu. Vui lòng thử lại sau.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  return {
    categories,
    articles,
    isLoading,
    error,
    categoryMap,
  };
};

export default useHome;
